import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
/* eslint-disable import/no-unresolved */
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
/* eslint-enable import/no-unresolved */
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import OutlinedInput from '@mui/material/OutlinedInput';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useStyles from './styles';

const defaultGetRowId = (row) => row.id;

const normalizeValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
};

const toComparableNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numberValue = Number(value);
  if (!Number.isNaN(numberValue)) {
    return numberValue;
  }

  const dateValue = Date.parse(value);
  if (!Number.isNaN(dateValue)) {
    return dateValue;
  }

  return null;
};

const getColumnValue = (row, column) => {
  if (!column || !column.field) {
    return '';
  }

  if (typeof column.valueGetter === 'function') {
    return column.valueGetter({
      row,
      field: column.field,
      value: row[column.field],
    });
  }

  return row[column.field];
};

/** Returns raw comparable value for sorting (number or timestamp). Used for date/number/price columns. */
const getSortValue = (row, column) => {
  let raw;
  if (column.type === 'date') {
    raw = typeof column.filterValueGetter === 'function'
      ? column.filterValueGetter({ row, field: column.field, value: row[column.field] })
      : row[column.field];
  } else {
    raw = typeof column.filterValueGetter === 'function'
      ? column.filterValueGetter({ row, field: column.field, value: row[column.field] })
      : getColumnValue(row, column);
  }
  if (raw instanceof Date) {
    return raw.getTime();
  }
  return toComparableNumber(raw);
};

const numericSortFn = (rowA, rowB, _columnId, columnDef) => {
  const a = getSortValue(rowA.original, columnDef);
  const b = getSortValue(rowB.original, columnDef);
  const aNum = a == null || Number.isNaN(a) ? null : Number(a);
  const bNum = b == null || Number.isNaN(b) ? null : Number(b);
  if (aNum === null && bNum === null) return 0;
  if (aNum === null) return 1;
  if (bNum === null) return -1;
  return aNum - bNum;
};

const formatColumnValue = (row, column) => {
  const rawValue = getColumnValue(row, column);

  if (typeof column.valueFormatter === 'function') {
    return column.valueFormatter({
      row,
      field: column.field,
      value: rawValue,
    });
  }

  return rawValue;
};

const exportRowsToCsv = (rows, columns) => {
  if (!rows?.length || !columns?.length) {
    return;
  }

  const headers = columns.map((column) => column.headerName || column.field);
  const csvRows = rows.map((row) => columns.map((column) => normalizeValue(formatColumnValue(row, column))));
  const csvContent = [headers, ...csvRows]
    .map((line) => line
      .map((value) => {
        const escapedValue = normalizeValue(value).replace(/"/g, '""');
        return /[",\n]/.test(escapedValue) ? `"${escapedValue}"` : escapedValue;
      })
      .join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `path_data_grid_${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const textFilter = (row, columnId, filterValue) => normalizeValue(row.getValue(columnId))
  .toLowerCase()
  .includes(normalizeValue(filterValue).toLowerCase().trim());

const selectFilter = (row, columnId, filterValue) => {
  if (!filterValue) {
    return true;
  }

  return normalizeValue(row.getValue(columnId)).toLowerCase() === normalizeValue(filterValue).toLowerCase();
};

const rangeFilter = (row, columnId, filterValue, columnDef) => {
  const min = filterValue?.min;
  const max = filterValue?.max;
  if (min == null && max == null) {
    return true;
  }

  const rawValue = columnDef
    ? (typeof columnDef.filterValueGetter === 'function'
      ? columnDef.filterValueGetter({
        row: row.original,
        field: columnDef.field,
        value: row.original[columnDef.field],
      })
      : getColumnValue(row.original, columnDef))
    : row.getValue(columnId);
  const comparableValue = toComparableNumber(rawValue);
  if (comparableValue === null) {
    return false;
  }

  const minValue = min != null && min !== '' ? toComparableNumber(min) : null;
  const maxValue = max != null && max !== '' ? toComparableNumber(max) : null;

  if (minValue !== null && comparableValue < minValue) {
    return false;
  }

  if (maxValue !== null && comparableValue > maxValue) {
    return false;
  }

  return true;
};

const dateRangeFilter = (row, columnId, filterValue, columnDef) => {
  const start = filterValue?.start;
  const end = filterValue?.end;
  if (!start && !end) {
    return true;
  }

  const rowValue = columnDef
    ? (typeof columnDef.filterValueGetter === 'function'
      ? columnDef.filterValueGetter({
        row: row.original,
        field: columnDef.field,
        value: row.original[columnDef.field],
      })
      : getColumnValue(row.original, columnDef))
    : row.getValue(columnId);
  const comparableValue = rowValue instanceof Date
    ? rowValue.getTime()
    : toComparableNumber(rowValue);
  if (comparableValue === null) {
    return false;
  }

  const startValue = start ? toComparableNumber(start) : null;
  const endValue = end ? toComparableNumber(end) : null;

  if (startValue !== null && comparableValue < startValue) {
    return false;
  }

  if (endValue !== null && comparableValue > endValue) {
    return false;
  }

  return true;
};

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || !filterValue.length) {
    return true;
  }

  const rowValue = normalizeValue(row.getValue(columnId)).toLowerCase().trim();
  return filterValue.some((v) => normalizeValue(v).toLowerCase().trim() === rowValue);
};

/** Column type to internal filter type. Types: text | number | date | price | multiple-choice | single-choice */
const getFilterTypeFromColumn = (column) => {
  if (column.filterType) {
    return column.filterType;
  }
  switch (column.type) {
    case 'date':
      return 'dateRange';
    case 'number':
    case 'price':
      return 'range';
    case 'multiple-choice':
      return 'multiSelect';
    case 'single-choice':
      return 'select';
    case 'text':
    default:
      return 'text';
  }
};

const getSelectOptions = (column, rows) => {
  if (Array.isArray(column.filterOptions) && column.filterOptions.length) {
    return column.filterOptions;
  }

  return [...new Set(rows
    .map((row) => normalizeValue(formatColumnValue(row, column)).trim())
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
};

const getRangeMinMax = (column, rows) => {
  let min = column.filterMin;
  let max = column.filterMax;
  const values = rows
    .map((row) => {
      const raw = typeof column.filterValueGetter === 'function'
        ? column.filterValueGetter({ row, field: column.field, value: row[column.field] })
        : getColumnValue(row, column);
      return toComparableNumber(raw);
    })
    .filter((v) => v !== null && !Number.isNaN(v));
  if (!values.length) {
    return { min: min ?? 0, max: max ?? 100 };
  }
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  return {
    min: min != null ? Math.min(min, dataMin) : dataMin,
    max: max != null ? Math.max(max, dataMax) : dataMax,
  };
};

const getDateRangeMinMax = (column, rows) => {
  const values = rows
    .map((row) => toComparableNumber(formatColumnValue(row, column)))
    .filter((v) => v !== null && !Number.isNaN(v));
  if (!values.length) {
    return { min: null, max: null };
  }
  return { min: Math.min(...values), max: Math.max(...values) };
};

/** Returns numeric value for a row/column (for sum). Used for price/number columns. */
const getNumericValueForSum = (row, column) => {
  const raw = typeof column.filterValueGetter === 'function'
    ? column.filterValueGetter({ row, field: column.field, value: row[column.field] })
    : getColumnValue(row, column);
  return toComparableNumber(raw);
};

const computeSum = (rows, column) => {
  let sum = 0;
  for (let i = 0; i < rows.length; i += 1) {
    const n = getNumericValueForSum(rows[i], column);
    if (n !== null && !Number.isNaN(n)) {
      sum += Number(n);
    }
  }
  return sum;
};

const SUM_SCOPE_ALL = 'all';
const SUM_SCOPE_PAGE = 'page';
const SUM_SCOPE_SELECTED = 'selected';

const resolveUpdater = (updater, prevValue) => (
  typeof updater === 'function' ? updater(prevValue) : updater
);

const toRowSelectionMap = (ids = []) => ids.reduce((acc, id) => ({
  ...acc,
  [String(id)]: true,
}), {});

function PathDataGrid({
  rows,
  columns,
  getRowId,
  pageSize,
  rowsPerPageOptions,
  checkboxSelection,
  selectionModel,
  onSelectionModelChange,
  toolbarActions,
  showToolbar,
  showExportButton,
  enableRowDelete,
  showColumnFilters,
  showColumnManager,
  defaultShowFiltersPanel,
  toolbarIconButtons,
  searchPlaceholder,
  onRefresh,
  onExport,
  onRowsDelete,
  initialSorting,
  loading,
  multiRowSelection,
  isRowSelectable,
  className,
}) {
  const { classes, cx } = useStyles();
  const [displayRows, setDisplayRows] = useState(rows);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState(initialSorting);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const [rowSelection, setRowSelection] = useState(toRowSelectionMap(selectionModel));
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [filtersModalOpen, setFiltersModalOpen] = useState(defaultShowFiltersPanel);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [sumPanelOpen, setSumPanelOpen] = useState(false);
  const [sumScopeByColumn, setSumScopeByColumn] = useState({});

  useEffect(() => {
    setDisplayRows(rows);
  }, [rows]);

  useEffect(() => {
    setPagination((prevState) => ({
      ...prevState,
      pageSize,
    }));
  }, [pageSize]);

  useEffect(() => {
    if (selectionModel !== undefined) {
      setRowSelection(toRowSelectionMap(selectionModel));
    }
  }, [selectionModel]);

  const tableColumns = useMemo(() => columns
    .filter((column) => column.field)
    .map((column) => {
      const filterType = getFilterTypeFromColumn(column);
      return {
        id: column.field,
        header: column.headerName || column.field,
        accessorFn: (row) => formatColumnValue(row, column),
        cell: (context) => {
          if (typeof column.renderCell === 'function') {
            return column.renderCell({
              row: context.row.original,
              value: context.getValue(),
              field: column.field,
            });
          }

          return context.getValue();
        },
        meta: {
          filterType,
          columnDef: column,
        },
        filterFn: (() => {
          if (filterType === 'dateRange') {
            return (row, columnId, filterValue) => dateRangeFilter(row, columnId, filterValue, column);
          }
          if (filterType === 'range') {
            return (row, columnId, filterValue) => rangeFilter(row, columnId, filterValue, column);
          }
          if (filterType === 'multiSelect') {
            return 'multiSelectFilter';
          }
          if (filterType === 'select') {
            return 'selectFilter';
          }
          return 'textFilter';
        })(),
        ...(column.type === 'date' || column.type === 'number' || column.type === 'price'
          ? { sortingFn: (rowA, rowB, columnId) => numericSortFn(rowA, rowB, columnId, column) }
          : {}),
        enableSorting: column.sortable !== false,
      };
    }), [columns]);

  useEffect(() => {
    const nextIds = tableColumns.map((column) => column.id);
    setColumnOrder((prevOrder) => {
      if (!prevOrder.length) {
        return nextIds;
      }

      const keptOrder = prevOrder.filter((id) => nextIds.includes(id));
      const missing = nextIds.filter((id) => !keptOrder.includes(id));
      return [...keptOrder, ...missing];
    });
  }, [tableColumns]);

  const idMap = useMemo(() => displayRows.reduce((acc, row) => ({
    ...acc,
    [String(getRowId(row))]: getRowId(row),
  }), {}), [displayRows, getRowId]);

  const emitSelection = useCallback((nextSelectionMap) => {
    const selectedIds = Object.keys(nextSelectionMap)
      .filter((id) => nextSelectionMap[id])
      .map((id) => (idMap[id] === undefined ? id : idMap[id]));

    onSelectionModelChange(selectedIds);
  }, [idMap, onSelectionModelChange]);

  const handleRowSelectionChange = useCallback((updater) => {
    setRowSelection((prevSelection) => {
      const nextSelection = resolveUpdater(updater, prevSelection);
      emitSelection(nextSelection);
      return nextSelection;
    });
  }, [emitSelection]);

  const hasSumSelectedScope = Object.values(sumScopeByColumn).includes(SUM_SCOPE_SELECTED);
  const effectiveCheckboxSelection = checkboxSelection || (sumPanelOpen && hasSumSelectedScope);
  const effectiveMultiRowSelection = multiRowSelection || (sumPanelOpen && hasSumSelectedScope);

  const table = useReactTable({
    data: displayRows,
    columns: tableColumns,
    filterFns: {
      textFilter,
      selectFilter,
      rangeFilter,
      dateRangeFilter,
      multiSelectFilter,
    },
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination,
      rowSelection,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue?.trim()) {
        return true;
      }

      return row.getVisibleCells().some((cell) => normalizeValue(cell.getValue())
        .toLowerCase()
        .includes(filterValue.toLowerCase().trim()));
    },
    getRowId: (originalRow) => String(getRowId(originalRow)),
    enableRowSelection: (row) => (isRowSelectable ? isRowSelectable(row.original) : true),
    enableMultiRowSelection: effectiveMultiRowSelection,
    autoResetPageIndex: false,
  });

  const filteredRows = useMemo(() => table.getFilteredRowModel().rows.map((row) => row.original), [table]);
  const pageRows = table.getPaginationRowModel().rows;
  const totalRows = table.getFilteredRowModel().rows.length;

  const handleExport = useCallback(() => {
    const exportColumns = columns.filter((column) => column.field && column.disableExport !== true);

    if (onExport) {
      onExport(filteredRows, exportColumns);
      return;
    }

    exportRowsToCsv(filteredRows, exportColumns);
  }, [columns, filteredRows, onExport]);

  const handleDeleteSelectedRows = useCallback(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (!selectedRows.length) {
      return;
    }

    const selectedSet = new Set(selectedRows.map((row) => row.id));
    const deletedRows = selectedRows.map((row) => row.original);

    setDisplayRows((prevRows) => prevRows.filter((row) => !selectedSet.has(String(getRowId(row)))));
    setRowSelection({});
    onSelectionModelChange([]);
    onRowsDelete(deletedRows);
  }, [getRowId, onRowsDelete, onSelectionModelChange, table]);

  const moveColumn = useCallback((columnId, direction) => {
    setColumnOrder((prevOrder) => {
      const index = prevOrder.indexOf(columnId);
      if (index === -1) {
        return prevOrder;
      }

      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= prevOrder.length) {
        return prevOrder;
      }

      const nextOrder = [...prevOrder];
      const [moved] = nextOrder.splice(index, 1);
      nextOrder.splice(nextIndex, 0, moved);
      return nextOrder;
    });
  }, []);

  const columnMenuOpen = Boolean(columnsMenuAnchor);
  const orderedColumns = table.getAllLeafColumns();
  const summableColumns = useMemo(() => orderedColumns.filter(
    (col) => col.columnDef.meta?.columnDef && (col.columnDef.meta.columnDef.type === 'price' || col.columnDef.meta.columnDef.type === 'number'),
  ), [orderedColumns]);

  const activeFilterCount = useMemo(() => columnFilters.filter(({ value }) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') {
      const v = value;
      if ((v.start != null && v.start !== '') || (v.end != null && v.end !== '')) return true;
      if ((v.min != null && v.min !== '') || (v.max != null && v.max !== '')) return true;
      return false;
    }
    return false;
  }).length, [columnFilters]);

  return (
    <div className={cx(classes.container, className)}>
      {showToolbar && (
        <Box className={classes.toolbar}>
          <IconButton className={classes.toolbarIconButton} onClick={onRefresh} disableFocusRipple disableRipple>
            <RefreshIcon />
          </IconButton>
          {showExportButton && (
            <Button className={classes.toolbarPrimaryButton} onClick={handleExport} startIcon={<DownloadIcon />}>
              Export CSV
            </Button>
          )}
          <TextField
            className={classes.searchField}
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            placeholder={searchPlaceholder}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {toolbarIconButtons.map((button) => (
            <IconButton
              key={button.id}
              className={cx(classes.settingsButton, button.active && classes.activeToolbarButton)}
              onClick={button.onClick}
              disabled={button.disabled}
              disableFocusRipple
              disableRipple
              title={button.title}
            >
              {button.icon}
            </IconButton>
          ))}
          {showColumnManager && (
            <>
              <IconButton
                className={classes.settingsButton}
                onClick={(event) => setColumnsMenuAnchor(event.currentTarget)}
                disableFocusRipple
                disableRipple
              >
                <SettingsIcon />
              </IconButton>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={columnMenuOpen}
                onClose={() => setColumnsMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box className={classes.columnsMenuHeader}>Columns</Box>
                {orderedColumns.map((column, index) => (
                  <MenuItem key={column.id} className={classes.columnsMenuItem}>
                    <Checkbox
                      size="small"
                      checked={column.getIsVisible()}
                      onChange={() => column.toggleVisibility()}
                    />
                    <Typography className={classes.columnsMenuLabel}>{column.columnDef.header}</Typography>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        moveColumn(column.id, 'up');
                      }}
                      disabled={index === 0}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        moveColumn(column.id, 'down');
                      }}
                      disabled={index === orderedColumns.length - 1}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {showColumnFilters && (
            <Badge
              badgeContent={activeFilterCount}
              color="error"
              showZero={false}
              className={classes.filtersBadge}
            >
              <IconButton
                className={cx(classes.settingsButton, filtersModalOpen && classes.activeToolbarButton)}
                onClick={() => setFiltersModalOpen(true)}
                disableFocusRipple
                disableRipple
                title="Filters"
              >
                <ManageSearchIcon />
              </IconButton>
            </Badge>
          )}
          <IconButton
            className={cx(classes.settingsButton, sumPanelOpen && classes.activeToolbarButton)}
            onClick={() => setSumPanelOpen((open) => !open)}
            disableFocusRipple
            disableRipple
            title="Sum price fields"
          >
            <CalculateIcon />
          </IconButton>
          <Box className={classes.toolbarActions}>
            {toolbarActions.map((action) => (
              <Button
                key={action.id || action.label}
                className={classes.actionButton}
                onClick={action.onClick}
                startIcon={action.startIcon}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
            {enableRowDelete && (
              <Button
                className={classes.actionButton}
                onClick={handleDeleteSelectedRows}
                startIcon={<DeleteIcon />}
                disabled={!table.getSelectedRowModel().rows.length}
              >
                Delete Row
              </Button>
            )}
          </Box>
        </Box>
      )}

      {showColumnFilters && (() => {
        const filterableColumns = columns.filter((column) => column.field && column.filterable !== false);
        const visible = (column) => table.getColumn(column.field)?.getIsVisible();
        const dateRangeColumns = filterableColumns.filter(
          (column) => visible(column) && getFilterTypeFromColumn(column) === 'dateRange',
        );
        const numberFilterColumns = filterableColumns.filter(
          (column) => visible(column) && getFilterTypeFromColumn(column) === 'range',
        );
        const selectFilterColumns = filterableColumns.filter(
          (column) => visible(column) && ['select', 'multiSelect'].includes(getFilterTypeFromColumn(column)),
        );
        const textFilterColumns = filterableColumns.filter(
          (column) => visible(column) && getFilterTypeFromColumn(column) === 'text',
        );
        const hasFilters = dateRangeColumns.length > 0 || numberFilterColumns.length > 0
          || selectFilterColumns.length > 0 || textFilterColumns.length > 0;

        return (
          <Dialog
            open={filtersModalOpen}
            onClose={() => setFiltersModalOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ className: classes.filtersModalPaper }}
          >
            <DialogTitle className={classes.filtersModalTitle}>Filters</DialogTitle>
            <DialogContent className={classes.filtersModalContent}>
              {!hasFilters ? (
                <Typography color="text.secondary">No filterable columns available.</Typography>
              ) : (
                <div className={classes.filtersContainer}>
                  {dateRangeColumns.length > 0 && (
                    <Box className={classes.dateFiltersSection}>
                      <Typography className={classes.dateFiltersSectionLabel}>Date range filters</Typography>
                      {dateRangeColumns.map((column) => {
                        const tableColumn = table.getColumn(column.field);
                        const filterValue = tableColumn?.getFilterValue();
                        const { min: minTs, max: maxTs } = getDateRangeMinMax(column, displayRows);
                        const minDate = minTs != null ? new Date(minTs) : null;
                        const maxDate = maxTs != null ? new Date(maxTs) : null;
                        return (
                          <Box key={column.field} className={classes.dateRangeFilter}>
                            <Typography variant="caption" className={classes.filterLabel}>
                              {column.headerName || column.field}
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label="From"
                                value={filterValue?.start ? dayjs(filterValue.start) : null}
                                onChange={(date) => tableColumn.setFilterValue({
                                  ...(filterValue || {}),
                                  start: date ? date.toISOString() : null,
                                })}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                minDate={minDate ? dayjs(minDate) : undefined}
                                maxDate={maxDate ? dayjs(maxDate) : undefined}
                              />
                              <DatePicker
                                label="To"
                                value={filterValue?.end ? dayjs(filterValue.end) : null}
                                onChange={(date) => tableColumn.setFilterValue({
                                  ...(filterValue || {}),
                                  end: date ? date.toISOString() : null,
                                })}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                minDate={minDate ? dayjs(minDate) : undefined}
                                maxDate={maxDate ? dayjs(maxDate) : undefined}
                              />
                            </LocalizationProvider>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {(numberFilterColumns.length > 0 || selectFilterColumns.length > 0 || textFilterColumns.length > 0) && (
                  <div className={classes.filterTypeSectionsGrid}>
                    {numberFilterColumns.length > 0 && (
                      <Box className={classes.filterSectionBox}>
                        <Typography className={classes.filterSectionLabel}>Number filters</Typography>
                        <div className={classes.filterSectionFields}>
                          {numberFilterColumns.map((column) => {
                            const tableColumn = table.getColumn(column.field);
                            const filterValue = tableColumn?.getFilterValue();
                            const { min: rangeMin, max: rangeMax } = getRangeMinMax(column, displayRows);
                            const sliderMin = Number(rangeMin);
                            const sliderMax = Number(rangeMax);
                            const valueMin = filterValue?.min !== undefined && filterValue?.min !== ''
                              ? Number(filterValue.min)
                              : sliderMin;
                            const valueMax = filterValue?.max !== undefined && filterValue?.max !== ''
                              ? Number(filterValue.max)
                              : sliderMax;
                            return (
                              <Box key={column.field} className={classes.rangeSliderFilter}>
                                <Typography variant="caption" className={classes.filterLabel}>
                                  {column.headerName || column.field}
                                </Typography>
                                <Slider
                                  value={[valueMin, valueMax]}
                                  min={sliderMin}
                                  max={sliderMax}
                                  valueLabelDisplay="auto"
                                  onChange={(_, newValue) => {
                                    const [minVal, maxVal] = newValue;
                                    tableColumn.setFilterValue({ min: minVal, max: maxVal });
                                  }}
                                  valueLabelFormat={(v) => (column.type === 'price' ? `£${Number(v)}` : v)}
                                  size="small"
                                  className={classes.rangeSlider}
                                />
                                <Box className={classes.rangeFilterInputs}>
                                  <TextField
                                    label="Min"
                                    type="number"
                                    size="small"
                                    value={filterValue?.min ?? ''}
                                    onChange={(e) => tableColumn.setFilterValue({
                                      ...(filterValue || { min: '', max: '' }),
                                      min: e.target.value === '' ? '' : Number(e.target.value),
                                    })}
                                    inputProps={{ min: sliderMin, max: sliderMax }}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Max"
                                    type="number"
                                    size="small"
                                    value={filterValue?.max ?? ''}
                                    onChange={(e) => tableColumn.setFilterValue({
                                      ...(filterValue || { min: '', max: '' }),
                                      max: e.target.value === '' ? '' : Number(e.target.value),
                                    })}
                                    inputProps={{ min: sliderMin, max: sliderMax }}
                                    fullWidth
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </div>
                      </Box>
                    )}
                    {selectFilterColumns.length > 0 && (
                      <Box className={classes.filterSectionBox}>
                        <Typography className={classes.filterSectionLabel}>Select filters</Typography>
                        <div className={classes.filterSectionFields}>
                          {selectFilterColumns.map((column) => {
                            const tableColumn = table.getColumn(column.field);
                            const filterValue = tableColumn?.getFilterValue();
                            const filterType = getFilterTypeFromColumn(column);
                            if (filterType === 'multiSelect') {
                              const options = getSelectOptions(column, displayRows);
                              const selected = Array.isArray(filterValue) ? filterValue : (filterValue ? [filterValue] : []);
                              return (
                                <FormControl key={column.field} size="small" fullWidth className={classes.filterFormControl}>
                                  <InputLabel>{column.headerName || column.field}</InputLabel>
                                  <Select
                                    multiple
                                    value={selected}
                                    onChange={(event) => {
                                      const v = event.target.value;
                                      tableColumn.setFilterValue(typeof v === 'string' ? v.split(',') : v);
                                    }}
                                    input={<OutlinedInput label={column.headerName || column.field} />}
                                    renderValue={(selectedValues) => (
                                      <Box className={classes.chipBox}>
                                        {selectedValues.map((val) => (
                                          <Chip key={val} size="small" label={val} className={classes.filterChip} />
                                        ))}
                                      </Box>
                                    )}
                                  >
                                    {options.map((option) => (
                                      <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              );
                            }
                            const options = getSelectOptions(column, displayRows);
                            return (
                              <TextField
                                key={column.field}
                                label={column.headerName || column.field}
                                value={filterValue || ''}
                                onChange={(event) => tableColumn.setFilterValue(event.target.value)}
                                size="small"
                                select
                                fullWidth
                              >
                                <MenuItem value="">All</MenuItem>
                                {options.map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </TextField>
                            );
                          })}
                        </div>
                      </Box>
                    )}
                    {textFilterColumns.length > 0 && (
                      <Box className={classes.filterSectionBox}>
                        <Typography className={classes.filterSectionLabel}>Text filters</Typography>
                        <div className={classes.filterSectionFields}>
                          {textFilterColumns.map((column) => {
                            const tableColumn = table.getColumn(column.field);
                            const filterValue = tableColumn?.getFilterValue();
                            return (
                              <TextField
                                key={column.field}
                                label={column.headerName || column.field}
                                value={filterValue || ''}
                                onChange={(event) => tableColumn.setFilterValue(event.target.value)}
                                size="small"
                                fullWidth
                              />
                            );
                          })}
                        </div>
                      </Box>
                    )}
                  </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions className={classes.filtersModalActions}>
              <Button
                onClick={() => {
                  setColumnFilters([]);
                  setGlobalFilter('');
                }}
                color="inherit"
              >
                Reset
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setFiltersModalOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <TableContainer className={classes.tableContainer}>
        {loading && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={28} />
          </Box>
        )}
        <Table stickyHeader size="small">
          <TableHead>
            {sumPanelOpen && (() => {
              const headerGroup = table.getHeaderGroups()[0];
              if (!headerGroup) return null;
              const rowsForAll = table.getFilteredRowModel().rows.map((r) => r.original);
              const rowsForPage = pageRows.map((r) => r.original);
              const rowsForSelected = table.getSelectedRowModel().rows.map((r) => r.original);
              return (
                <TableRow className={classes.sumRow}>
                  {effectiveCheckboxSelection && (
                    <TableCell padding="checkbox" className={classes.sumCell} />
                  )}
                  {headerGroup.headers.map((header) => {
                    const col = header.column;
                    const columnDef = col.columnDef.meta?.columnDef;
                    const isSummable = columnDef && (columnDef.type === 'price' || columnDef.type === 'number');
                    const scope = sumScopeByColumn[col.id] ?? SUM_SCOPE_ALL;
                    if (!isSummable) {
                      return <TableCell key={header.id} className={classes.sumCell} />;
                    }
                    const rowsToSum = scope === SUM_SCOPE_ALL
                      ? rowsForAll
                      : scope === SUM_SCOPE_PAGE
                        ? rowsForPage
                        : rowsForSelected;
                    const sumValue = computeSum(rowsToSum, columnDef);
                    const displaySum = columnDef.type === 'price'
                      ? `£${Number(sumValue).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : Number(sumValue).toLocaleString();
                    return (
                      <TableCell key={header.id} className={classes.sumCell}>
                        <Box className={classes.sumCellContent}>
                          <Select
                            size="small"
                            value={scope}
                            onChange={(e) => setSumScopeByColumn((prev) => ({ ...prev, [col.id]: e.target.value }))}
                            className={classes.sumScopeSelect}
                            variant="outlined"
                          >
                            <MenuItem value={SUM_SCOPE_ALL}>Sum all</MenuItem>
                            <MenuItem value={SUM_SCOPE_PAGE}>Sum page</MenuItem>
                            <MenuItem value={SUM_SCOPE_SELECTED}>Sum selected</MenuItem>
                          </Select>
                          <Typography variant="body2" className={classes.sumValue}>
                            {displaySum}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })()}
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {effectiveCheckboxSelection && (
                  <TableCell padding="checkbox" className={classes.headerCell}>
                    <Checkbox
                      size="small"
                      indeterminate={table.getIsSomePageRowsSelected()}
                      checked={table.getIsAllPageRowsSelected()}
                      onClick={(event) => event.stopPropagation()}
                      onChange={table.getToggleAllPageRowsSelectedHandler()}
                    />
                  </TableCell>
                )}
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id} className={classes.headerCell}>
                    {header.isPlaceholder ? null : (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                        onClick={header.column.getToggleSortingHandler()}
                        hideSortIcon={!header.column.getIsSorted()}
                        IconComponent={ArrowDownwardIcon}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody className={loading ? classes.tableBodyLoading : ''}>
            {!pageRows.length && (
              <TableRow>
                <TableCell
                  className={classes.emptyState}
                  colSpan={table.getVisibleLeafColumns().length + (effectiveCheckboxSelection ? 1 : 0)}
                >
                  No rows found
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={row.getIsSelected()}
                onClick={() => {
                  if (!row.getCanSelect()) {
                    return;
                  }

                  if (effectiveMultiRowSelection) {
                    handleRowSelectionChange((prevSelection) => ({
                      ...prevSelection,
                      [row.id]: !row.getIsSelected(),
                    }));
                    return;
                  }

                  const nextSelection = {};
                  nextSelection[row.id] = !row.getIsSelected();
                  handleRowSelectionChange(nextSelection);
                }}
                className={cx(classes.clickableRow, row.getIsSelected() && classes.selectedRow)}
              >
                {effectiveCheckboxSelection && (
                  <TableCell padding="checkbox" className={classes.bodyCell}>
                    <Checkbox
                      size="small"
                      disabled={!row.getCanSelect()}
                      checked={row.getIsSelected()}
                      onClick={(event) => event.stopPropagation()}
                      onChange={row.getToggleSelectedHandler()}
                    />
                  </TableCell>
                )}
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={classes.bodyCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className={classes.pagination}
        component="div"
        rowsPerPageOptions={rowsPerPageOptions}
        count={totalRows}
        rowsPerPage={pagination.pageSize}
        page={pagination.pageIndex}
        onPageChange={(_event, nextPage) => table.setPageIndex(nextPage)}
        onRowsPerPageChange={(event) => {
          table.setPageSize(Number(event.target.value));
          table.setPageIndex(0);
        }}
      />
    </div>
  );
}

PathDataGrid.propTypes = {
  checkboxSelection: PropTypes.bool,
  className: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    disableExport: PropTypes.bool,
    field: PropTypes.string,
    filterMax: PropTypes.number,
    filterMin: PropTypes.number,
    filterOptions: PropTypes.arrayOf(PropTypes.string),
    filterType: PropTypes.oneOf(['text', 'select', 'range', 'dateRange', 'multiSelect']),
    filterable: PropTypes.bool,
    filterValueGetter: PropTypes.func,
    headerName: PropTypes.string,
    renderCell: PropTypes.func,
    sortable: PropTypes.bool,
    type: PropTypes.oneOf(['text', 'number', 'date', 'price', 'multiple-choice', 'single-choice']),
    valueFormatter: PropTypes.func,
    valueGetter: PropTypes.func,
  })).isRequired,
  enableRowDelete: PropTypes.bool,
  getRowId: PropTypes.func,
  initialSorting: PropTypes.arrayOf(PropTypes.shape({
    desc: PropTypes.bool,
    id: PropTypes.string,
  })),
  isRowSelectable: PropTypes.func,
  loading: PropTypes.bool,
  multiRowSelection: PropTypes.bool,
  onExport: PropTypes.func,
  onRefresh: PropTypes.func,
  onRowsDelete: PropTypes.func,
  onSelectionModelChange: PropTypes.func,
  pageSize: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  searchPlaceholder: PropTypes.string,
  selectionModel: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  defaultShowFiltersPanel: PropTypes.bool,
  showColumnFilters: PropTypes.bool,
  showColumnManager: PropTypes.bool,
  showExportButton: PropTypes.bool,
  showToolbar: PropTypes.bool,
  toolbarIconButtons: PropTypes.arrayOf(PropTypes.shape({
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    icon: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    title: PropTypes.string,
  })),
  toolbarActions: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    startIcon: PropTypes.node,
  })),
};

PathDataGrid.defaultProps = {
  checkboxSelection: true,
  className: '',
  enableRowDelete: false,
  getRowId: defaultGetRowId,
  initialSorting: [],
  isRowSelectable: null,
  loading: false,
  multiRowSelection: false,
  onExport: null,
  onRefresh: null,
  onRowsDelete: null,
  onSelectionModelChange: () => {},
  pageSize: 20,
  rowsPerPageOptions: [10, 20, 50, 100],
  searchPlaceholder: 'Search...',
  selectionModel: undefined,
  defaultShowFiltersPanel: false,
  showColumnFilters: false,
  showColumnManager: true,
  showExportButton: true,
  showToolbar: true,
  toolbarIconButtons: [],
  toolbarActions: [],
};

export default PathDataGrid;
